/* eslint-disable no-useless-escape */
import * as yup from 'yup';
import { AnySchema } from 'yup';
import Lazy from 'yup/lib/Lazy';
import Reference from 'yup/lib/Reference';

import images from '../../../assets/images';
import { EbookWritingType, SectionTargetExploreEbookBundleSortBy, SectionTargetExploreEbookBundleType, SortType } from '../../../core/graphql/types';
// import key from '../../../fictionlog/components/icons/components/Key';
import { IErrorObject } from '../../../utils/validator';
import { USER_PROFILE_TAB_KEYS } from '../components/UserProfileSectionItem';

export const PINTO_SHORTCUT_TYPE_MAP: Record<string, string> = {
  'ebook-shortcut': 'pinto-ebook',
  'ebook-bundle-shortcut': 'pinto-ebook-bundle',
  'product-set-shortcut': 'pinto-product-set',
  'search-shortcut': 'pinto-search',
  'explore-shortcut': 'pinto-explore',
  'explore-bundle-shortcut': 'pinto-explore-bundle',
  'link-shortcut': 'pinto-link',
  'user-profile-shortcut': 'pinto-user-profile',
  'pinto-feed-shortcut': 'pinto-feed',
  'ebook-collection-shortcut': 'pinto-ebook-collection',
  'explore-ebook-collection-shortcut': 'pinto-explore-ebook-collection',
  'pinto-custom-link': 'pinto-custom-link',
};

export const PINTO_SHORTCUT_TYPE_MAP_REVERSE: Record<string, string> = Object.fromEntries(Object.entries(PINTO_SHORTCUT_TYPE_MAP).map(([k, v]) => [v, k]));

export enum SECTION_BUILDER_FIELD_KEYS {
  TITLE = 'title',
  TARGET = 'target',
  LAYOUT_ID = 'layoutId',
  CONFIG_ITEMS = 'configItems',
  NOTE = 'note',
  // Add new keys for marquee_announcement
  ANNOUNCEMENT_TEXT = 'announcementText',
  TEXT_LIGHT_COLOR_HEXCODE = 'textLightColorHexcode',
  TEXT_DARK_COLOR_HEXCODE = 'textDarkColorHexcode',
  BG_LIGHT_COLOR_HEXCODE1 = 'bgLightColorHexcode1',
  BG_DARK_COLOR_HEXCODE1 = 'bgDarkColorHexcode1',
  BG_LIGHT_COLOR_HEXCODE2 = 'bgLightColorHexcode2',
  BG_DARK_COLOR_HEXCODE2 = 'bgDarkColorHexcode2',
  GRADIENT_MODE = 'gradientMode',
  BANNER_FULL_WIDTH_IMAGE_URL = 'bannerFullWidthImageUrl',
  BOOK_COVER_WITH_BANNER_IMAGE_URL = 'imageUrl',
  BOOK_COVER_WITH_BANNER_DAY_BG_IMAGE_URL = 'dayBgImageUrl',
  BOOK_COVER_WITH_BANNER_DARK_BG_IMAGE_URL = 'darkBgImageUrl',
}

export interface IErrorsSectionBuilder {
  title?: IErrorObject;
  // pref?: IErrorObject;
  // productType?: IErrorObject;
  contentType?: IErrorObject;
  layoutId?: IErrorObject;
  target?: IErrorObject;
  configItems?: IErrorObject;
  note?: IErrorObject;
  // isNCContent?: IErrorObject;
  // categoryTagId?: IErrorObject;
  // Add new fields for marquee_announcement
  announcementText?: IErrorObject;
  textLightColorHexcode?: IErrorObject;
  textDarkColorHexcode?: IErrorObject;
  bgLightColorHexcode1?: IErrorObject;
  bgDarkColorHexcode1?: IErrorObject;
  bgLightColorHexcode2?: IErrorObject;
  bgDarkColorHexcode2?: IErrorObject;
  gradientMode?: IErrorObject;
  bannerFullWidthImageUrl?: IErrorObject;
  imageUrl?: IErrorObject;
  dayBgImageUrl?: IErrorObject;
  darkBgImageUrl?: IErrorObject;
}

export type ISectionBuilderFields = keyof IErrorsSectionBuilder;

// export type ISectionLayoutId = 'BookCover' | 'BookCover2' | 'BookBanner' | 'LinkBanner' | 'LinkBannerFullWidth' | 'MainBanner' | 'TagCloud' | 'Tank' | 'Testimonial';

export enum ISectionLayoutId {
  BookBanner = 'book_banner',
  SquareCover = 'square_cover',
  BookCover = 'book_cover',
  BookCover2 = 'book_cover_2',
  // LinkBanner = 'link_banner',
  // LinkBannerFullWidth = 'link_banner_fullWidth',
  MainBanner = 'main_banner',
  TagCloud = 'tag_cloud',
  Rank = 'rank',
  // Testimonial = 'testimonial',
  EditorPick = 'editor_review',
  Shortcut = 'shortcut',
  // Marquee Announcement Bar
  MarqueeAnnouncement = 'marquee_announcement',
  BannerFullWidth = 'banner_full_width',
  BookCoverWithBanner = 'book_cover_with_banner',
}

export enum IProduct_SET_SORT_BY {
  Created = 'created',
  Updated = 'updated',
  Published = 'published',
  Random = 'random',
  LatestAdded = 'latestAdded',
  Trending = 'trending',
  Bestseller7Days = 'bestseller7Days',
  Bestseller30Days = 'bestseller30Days',
  Bestseller = 'bestseller',
  PriceThb = 'priceThb',
  Title = 'title',
}

export const SECTION_LAYOUT_OPTIONS = [
  { layoutId: ISectionLayoutId.BookBanner, label: 'Book Banner', imageUrl: images.pinto.sections.bookBanner },
  { layoutId: ISectionLayoutId.BookCover, label: 'Book Cover', imageUrl: images.pinto.sections.bookCover },
  { layoutId: ISectionLayoutId.SquareCover, label: 'Square Bundle', imageUrl: images.pinto.sections.squareCover },
  { layoutId: ISectionLayoutId.BookCover2, label: 'Book Cover 2', imageUrl: images.pinto.sections.bookCover2 },
  { layoutId: ISectionLayoutId.Rank, label: 'Rank', imageUrl: images.pinto.sections.rank },
  { layoutId: ISectionLayoutId.MainBanner, label: 'Main Banner', imageUrl: images.pinto.sections.mainBanner },
  { layoutId: ISectionLayoutId.TagCloud, label: 'Tag Cloud', imageUrl: images.pinto.sections.tagCloud },
  { layoutId: ISectionLayoutId.EditorPick, label: 'Editor`s Pick', imageUrl: images.pinto.sections.editorPick },
  { layoutId: ISectionLayoutId.Shortcut, label: 'Shortcut', imageUrl: images.pinto.sections.circleThumbnailShortcut },
  { layoutId: ISectionLayoutId.MarqueeAnnouncement, label: 'Marquee Announcement', imageUrl: images.pinto.sections.marqueeAnnouncement },
  { layoutId: ISectionLayoutId.BannerFullWidth, label: 'Banner Full Width', imageUrl: images.pinto.sections.bannerFullWidth },
  { layoutId: ISectionLayoutId.BookCoverWithBanner, label: 'Book Cover With Banner', imageUrl: images.pinto.sections.bookCoverWithBanner },

  // { layoutId: ISectionLayoutId.LinkBanner, label: 'Link Banner', imageUrl: images.pinto.sections.linkBanner },
  // { layoutId: ISectionLayoutId.LinkBannerFullWidth, label: 'Link Banner FullWidth', imageUrl: images.pinto.sections.linkBannerFullWidth },
  // { layoutId: ISectionLayoutId.Testimonial, label: 'Testimonial', imageUrl: images.pinto.sections.testimonial },
];

export const USER_PROFILE_TAB_OPTIONS = [
  { label: 'ทั้งหมด', value: null },
  { label: 'ขายดี', value: USER_PROFILE_TAB_KEYS.BEST_SELLER },
  { label: 'มาใหม่', value: USER_PROFILE_TAB_KEYS.NEW_ARRIVAL },
  { label: 'โปรโมชั่น', value: USER_PROFILE_TAB_KEYS.PROMOTION },
  { label: 'อีบุ๊กจัดชุด', value: USER_PROFILE_TAB_KEYS.BUNDLE },
  { label: 'เซ็ตหนังสือ', value: USER_PROFILE_TAB_KEYS.PRODUCT_SET },
  { label: 'แนะนำ', value: USER_PROFILE_TAB_KEYS.EBOOK_COLLECTIONS },
];

export const SORT_BY_QUERY_EBOOK_OPTIONS = [
  {
    label: 'ไม่ระบุ',
    value: [
      { keyName: 'sortBy', value: null },
      { keyName: 'sortType', value: null },
    ],
  },
  {
    label: 'ยอดวิว',
    value: [
      { keyName: 'sortBy', value: 'viewsCount' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'เผยแพร่ล่าสุด',
    value: [
      { keyName: 'sortBy', value: 'published' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'สร้างล่าสุด',
    value: [
      { keyName: 'sortBy', value: 'created' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'เทรนดิ้ง',
    value: [
      { keyName: 'sortBy', value: 'trending' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ยอดนิยมตลอดกาล',
    value: [
      { keyName: 'sortBy', value: 'popular' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ยอดนิยม 7 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: 'popular7Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ยอดนิยม 30 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: 'popular30Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ขายดี 7 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: 'bestseller7Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ขายดี 30 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: 'bestseller30Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย

  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: null },
    ],
  },

  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: 'title' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย (ถ้าไม่ส่ง default จะเป็น desc ซึ่งหมายความว่าจะเรียงแบบ ฮ - ก )

  {
    label: 'สุ่ม',
    value: [
      { keyName: 'sortBy', value: 'random' },
      { keyName: 'sortType', value: null },
    ],
  },
];

export const SORT_BY_QUERY_EBOOK_BUNDLE_OPTIONS = [
  {
    label: 'ไม่ระบุ',
    value: [
      { keyName: 'sortBy', value: null },
      { keyName: 'sortType', value: null },
    ],
  },
  {
    label: 'โปรโมชันใกล้หมดอายุ',
    value: [
      { keyName: 'sortBy', value: 'discountEnded' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'เผยแพร่ล่าสุด',
    value: [
      { keyName: 'sortBy', value: 'started' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'สร้างล่าสุด',
    value: [
      { keyName: 'sortBy', value: 'created' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ขายดี 7 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.BestSeller7Days },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ขายดี 30 วันที่แล้ว',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.BestSeller30Days },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  // {
  //   label: 'ขายดีตลอดกาล',
  //   value: [
  //     { keyName: 'sortBy', value: 'bestSeller' },
  //     { keyName: 'sortType', value: SortType.Desc },
  //   ],
  // },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย

  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },

  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: 'title' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย (ถ้าไม่ส่ง default จะเป็น desc ซึ่งหมายความว่าจะเรียงแบบ ฮ - ก )

  {
    label: 'สุ่ม',
    value: [
      { keyName: 'sortBy', value: 'random' },
      { keyName: 'sortType', value: null },
    ],
  },
];

//TODO: recheck
export const SORT_BY_SEARCH_OPTIONS = [
  {
    label: 'ที่เกี่ยวข้อง',
    value: [
      { keyName: 'sortBy', value: 'relevant' },
      { keyName: 'sortType', value: null },
    ],
  },
  {
    label: 'วันที่เผยแพร่',
    value: [
      { keyName: 'sortBy', value: 'published' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },

  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: 'title' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
];

export const SORT_BY_EXPLORE_OPTIONS = [
  {
    label: 'วันที่เผยแพร่',
    value: [
      { keyName: 'sortBy', value: 'published' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: 'priceThb' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: 'title' },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
];

export const SORT_BY_EXPLORE_BEST_SELLER_OPTIONS = [
  {
    label: '7 วัน',
    value: [
      { keyName: 'sortBy', value: 'bestseller7Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: '30 วัน',
    value: [
      { keyName: 'sortBy', value: 'bestseller30Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'ตลอดกาล',
    value: [
      { keyName: 'sortBy', value: 'bestseller' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
];

export const SORT_BY_EXPLORE_ITEM_BEST_SELLER_OPTIONS = [
  {
    label: '7 วัน',
    value: [
      { keyName: 'sortBy', value: 'bestSeller7Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: '30 วัน',
    value: [
      { keyName: 'sortBy', value: 'bestSeller30Days' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'ตลอดกาล',
    value: [
      { keyName: 'sortBy', value: 'bestSeller' },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
];

export const SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS = [
  {
    label: '7 วัน',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.BestSeller7Days },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: '30 วัน',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.BestSeller30Days },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
];

export const SORT_BY_EXPLORE_BUNDLES_OPTIONS = [
  {
    label: 'โปรโมชันใกล้หมดอายุ',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.DiscountEnded },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'วันที่เผยแพร่',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.Started },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.PriceThb },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.PriceThb },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: SectionTargetExploreEbookBundleSortBy.Title },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
];

export const SORT_BY_PRODUCT_SET_OPTIONS = [
  {
    label: 'เผยแพร่ล่าสุด',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Published },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'เพิ่มล่าสุด',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.LatestAdded },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'เทรนดิ้ง',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Trending },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'ขายดี 7 วัน',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Bestseller7Days },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'ขายดี 30 วัน',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Bestseller30Days },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'ขายดีตลอดกาล',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Bestseller },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'ราคาสูง-ต่ำ',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.PriceThb },
      { keyName: 'sortType', value: SortType.Desc },
    ],
  },
  {
    label: 'ราคาต่ำ-สูง',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.PriceThb },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  }, // และต้องส่ง sortType เป็น asc มาด้วย
  {
    label: 'เรียงตามตัวอักษร',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Title },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
  {
    label: 'สุ่ม',
    value: [
      { keyName: 'sortBy', value: IProduct_SET_SORT_BY.Random },
      { keyName: 'sortType', value: SortType.Asc },
    ],
  },
];

export const RATING_OPTIONS = [
  { label: 'ทั้งหมด', value: null },
  { label: '≥ 4 Stars', value: 4 },
  { label: '≥ 3 Stars', value: 3 },
  { label: '≥ 2 Stars', value: 2 },
  { label: '≥ 1 Stars', value: 1 },
];

export const IS_FREE_OPTIONS = [
  {
    label: 'ทั้งหมด',
    value: [{ keyName: 'isFree', value: '' }],
  },
  {
    label: 'ฟรี',
    value: [
      { keyName: 'isFree', value: true },
      { keyName: 'hasDiscount', value: null },
      { keyName: 'lteDiscountRatio', value: null },
      { keyName: 'gteDiscountRatio', value: null },
    ],
  },
  {
    label: 'ไม่ฟรี',
    value: [{ keyName: 'isFree', value: false }],
  },
];

export const EBOOK_IS_FREE_OPTIONS = [
  { label: 'ทั้งหมด', value: '' },
  { label: 'ฟรี', value: true },
  { label: 'ไม่ฟรี', value: false },
];

export const EBOOK_HAS_DISCOUNT = [
  {
    label: 'ทั้งหมด',
    value: [
      { keyName: 'hasDiscount', value: null },
      { keyName: 'lteDiscountRatio', value: null },
      { keyName: 'gteDiscountRatio', value: null },
    ],
  },
  {
    label: 'ไม่มีส่วนลด',
    value: [
      { keyName: 'hasDiscount', value: false },
      { keyName: 'lteDiscountRatio', value: null },
      { keyName: 'gteDiscountRatio', value: null },
    ],
  },
  { label: 'มีส่วนลด', value: [{ keyName: 'hasDiscount', value: true }] },
];

export const EBOOK_HAS_DISCOUNT_END_DATE = [
  {
    label: 'ทั้งหมด',
    value: 'null',
  },
  {
    label: 'ส่วนลดแบบไม่มีระยะเวลาสิ้นสุด',
    value: 'false',
  },
  { label: 'ส่วนลดแบบมีระยะเวลาสิ้นสุด', value: 'true' },
];

export const EBOOK_TYPE_OPTIONS = [
  { label: 'ทั้งหมด', value: [] },
  { label: 'อีบุ๊กไทย', value: ['thai'] },
  { label: 'อีบุ๊กแปล', value: ['translatedThai'] },
];
export const EBOOK_FILE_TYPE_OPTIONS = [
  { label: 'ทั้งหมด', value: [] },
  { label: 'PDF', value: ['pdf'] },
  { label: 'EPUB', value: ['epub'] },
];
export const PRICE_SETTING_OPTIONS = [
  { label: 'ทั้งหมด', value: [] },
  { label: 'ฟรี', value: ['free'] },
  { label: 'กำลังลดราคา', value: ['onSale'] },
  { label: 'ฟรีหรือกำลังลดราคา', value: ['free', 'onSale'] },
];

export const BUNDLES_PRICE_SETTING_OPTIONS = [
  { label: 'ทั้งหมด', value: [] },
  { label: 'กำลังลดราคา', value: ['onSale'] },
];

export const BUNDLES_CATEGORY_OPTIONS = [
  { label: 'ทั้งหมด', value: null },
  { label: 'นิยาย', value: 1 },
  { label: 'การ์ตูน', value: 2 },
  { label: 'อีบุ๊กทั่วไป', value: 3 },
  { label: 'นิตยสาร', value: 99 },
  { label: 'หนังสือพิมพ์', value: 102 },
];

export const CARTOON_TYPE_OPTIONS = [
  { label: 'ทั้งหมด', value: [] },
  { label: 'รวมเล่ม', value: [EbookWritingType.Bundle] },
  { label: 'รายตอน', value: [EbookWritingType.Chapter] },
];

export const SPECIAL_SEARCH_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Title', value: 'title' },
  { label: 'Tag', value: 'tag' },
  { label: 'Author/Translator', value: 'authorOrTranslator' },
];

export enum EXPLORE_TYPE_VALUE {
  Trending = 'trending',
  BestSeller = 'bestseller',
  NewArrival = 'newArrival',
  Free = 'free',
  All = 'all',
}

export const EXPLORE_TYPE_OPTIONS = [
  { label: 'เทรนดิ้ง', value: EXPLORE_TYPE_VALUE.Trending },
  { label: 'ขายดี', value: EXPLORE_TYPE_VALUE.BestSeller },
  { label: 'มาใหม่', value: EXPLORE_TYPE_VALUE.NewArrival },
  { label: 'ฟรีอีบุ๊ก', value: EXPLORE_TYPE_VALUE.Free },
  { label: 'ทั้งหมด', value: EXPLORE_TYPE_VALUE.All },
];

export enum EXPLORE_TYPE_ITEM_VALUE {
  NewArrival = 'newArrival',
  Trending = 'trending',
  BestSeller = 'bestSeller',
  Free = 'free',
  All = 'all',
}

export const EXPLORE_TYPE_ITEM_OPTIONS = [
  { label: 'เทรนดิ้ง', value: EXPLORE_TYPE_ITEM_VALUE.Trending },
  { label: 'ขายดี', value: EXPLORE_TYPE_ITEM_VALUE.BestSeller },
  { label: 'มาใหม่', value: EXPLORE_TYPE_ITEM_VALUE.NewArrival },
  { label: 'ฟรีอีบุ๊ก', value: EXPLORE_TYPE_ITEM_VALUE.Free },
  { label: 'ทั้งหมด', value: EXPLORE_TYPE_ITEM_VALUE.All },
];

export const EXPLORE_BUNDLES_TAB_OPTIONS = [
  { label: 'ขายดี', value: SectionTargetExploreEbookBundleType.BestSeller },
  { label: 'มาใหม่', value: SectionTargetExploreEbookBundleType.NewArrival },
  { label: 'ทั้งหมด', value: SectionTargetExploreEbookBundleType.All },
];

export const SEARCH_TYPE_OPTIONS = [
  { label: 'Ebook', value: [{ keyName: 'searchType', value: 'ebook' }] },
  {
    label: 'User',
    value: [
      { keyName: 'searchType', value: 'user' },
      { keyName: 'searchField', value: null },
      { keyName: 'sortBy', value: null },
      { keyName: 'sortType', value: null },
      { keyName: 'categoryId', value: null },
      { keyName: 'gteRating', value: null },
      { keyName: 'anyTypes', value: null },
      { keyName: 'anyPriceTypes', value: null },
    ],
  },
];

export enum ISectionTargetType {
  NONE = '',
  FEED = 'feed',
  LINK = 'link',
  DEEP_LINK = 'deep-link',
  USER = 'user',
  SEARCH = 'search',
  EXPLORE = 'explore',
  EXPLORE_BUNDLES = 'explore-ebook-bundle',
  PRODUCT_SET = 'explore-product-set',
}

export const TARGET_TYPE_OPTIONS = {
  none: { label: 'ไม่ระบุ', value: '' },
  feed: { label: 'Feed', value: ISectionTargetType.FEED },
  link: { label: 'Link', value: ISectionTargetType.LINK },
  'deep-link': { label: 'Link', value: ISectionTargetType.DEEP_LINK },
  user: { label: 'User', value: ISectionTargetType.USER },
  explore: { label: 'Explore', value: ISectionTargetType.EXPLORE },
  'explore-ebook-bundle': { label: 'Explore Bundles', value: ISectionTargetType.EXPLORE_BUNDLES },
  search: { label: 'Search', value: ISectionTargetType.SEARCH },
  'explore-product-set': { label: 'Product set', value: ISectionTargetType.PRODUCT_SET },
};

export const TARGET_SEARCH_TYPE_OPTIONS = [
  { label: 'อีบุ๊ก', value: 'ebook' },
  { label: 'ผู้ใช้', value: 'user' },
];

export const TARGET_SEARCH_FIELD_OPTIONS = [
  { label: 'ทั้งหมด', value: 'All' },
  { label: 'ชื่อเรื่อง', value: 'Title' },

  { label: 'แท็ก', value: 'Tag' },
  { label: 'นักเขียน/นักแปล', value: 'AuthorOrTranslator' },
];

//TODO: use only section type in soft lunch
export enum IConfigItemType {
  Ebook = 'ebook',
  EbookBundle = 'ebook-bundle',
  EbookCollection = 'ebook-collection',
  ExploreEbookCollection = 'explore-ebook-collection',

  Feed = 'feed',
  Link = 'link',
  QueryEbook = 'query-ebook',
  QueryEbookBundle = 'query-ebook-bundle',

  Search = 'search',
  // Explore = 'explore',
  // User = 'user',
  ProductSet = 'product-set',
  QueryProductSet = 'query-product-set',
  EditorReviewEbook = 'editor-review-ebook',
  UserProfile = 'user-profile',

  // ─── “shortcut” TYPES ───────────
  EbookShortcut = 'ebook-shortcut',
  EbookBundleShortcut = 'ebook-bundle-shortcut',
  ProductSetShortcut = 'product-set-shortcut',
  LinkShortcut = 'link-shortcut',
  SearchShortcut = 'search-shortcut',
  ExploreShortcut = 'explore-shortcut',
  ExploreBundleShortcut = 'explore-bundle-shortcut',
  PintoFeedShortcut = 'pinto-feed-shortcut',
  UserProfileShortcut = 'user-profile-shortcut',
  EbookCollectionShortcut = 'ebook-collection-shortcut',
  ExploreEbookCollectionShortcut = 'explore-ebook-collection-shortcut',
  PintoCustomLink = 'pinto-custom-link',
}

export const ADD_ITEM_OPTION_BY_LAYOUT_ID = {
  bookBanner: [
    IConfigItemType.Ebook,
    IConfigItemType.QueryEbook,
    IConfigItemType.ProductSet,
    IConfigItemType.QueryProductSet,
    IConfigItemType.EbookBundle,
    IConfigItemType.QueryEbookBundle,
  ],
  squareCover: [IConfigItemType.EbookBundle, IConfigItemType.QueryEbookBundle],
  bookCover: [IConfigItemType.Ebook, IConfigItemType.QueryEbook],
  bookCoverWithBanner: [IConfigItemType.Ebook, IConfigItemType.QueryEbook],
  bookCover2: [IConfigItemType.Ebook, IConfigItemType.QueryEbook],
  mainBanner: [IConfigItemType.Ebook, IConfigItemType.Feed, IConfigItemType.Link, IConfigItemType.Search, IConfigItemType.EbookBundle, IConfigItemType.QueryEbookBundle],
  rank: [IConfigItemType.Ebook, IConfigItemType.QueryEbook],
  tagCloud: [IConfigItemType.Search],
  editorPick: [IConfigItemType.EditorReviewEbook],
  shortcut: [
    IConfigItemType.EbookShortcut,
    IConfigItemType.EbookBundleShortcut,
    IConfigItemType.EbookCollectionShortcut,
    IConfigItemType.ExploreShortcut,
    IConfigItemType.ExploreBundleShortcut,
    IConfigItemType.ExploreEbookCollectionShortcut,
    IConfigItemType.SearchShortcut,
    IConfigItemType.ProductSetShortcut,
    IConfigItemType.LinkShortcut,
    IConfigItemType.PintoFeedShortcut,
    IConfigItemType.UserProfileShortcut,
    IConfigItemType.PintoCustomLink,
  ],
  marqueeAnnouncement: [
    IConfigItemType.EbookShortcut,
    IConfigItemType.EbookBundleShortcut,
    IConfigItemType.EbookCollectionShortcut,
    IConfigItemType.ExploreShortcut,
    IConfigItemType.ExploreBundleShortcut,
    IConfigItemType.ExploreEbookCollectionShortcut,
    IConfigItemType.SearchShortcut,
    IConfigItemType.ProductSetShortcut,
    IConfigItemType.LinkShortcut,
    IConfigItemType.PintoFeedShortcut,
    IConfigItemType.UserProfileShortcut,
  ],
  banner_full_width: [
    IConfigItemType.EbookShortcut,
    IConfigItemType.EbookBundleShortcut,
    IConfigItemType.EbookCollectionShortcut,
    IConfigItemType.ExploreShortcut,
    IConfigItemType.ExploreBundleShortcut,
    IConfigItemType.ExploreEbookCollectionShortcut,
    IConfigItemType.SearchShortcut,
    IConfigItemType.ProductSetShortcut,
    IConfigItemType.LinkShortcut,
    IConfigItemType.PintoFeedShortcut,
    IConfigItemType.UserProfileShortcut,
  ],
  // linkBanner: []
  // linkBannerFullWidth: [],
  // testimonial: [],
};

export const TARGET_OPTION_BY_LAYOUT_ID = {
  book_banner: [
    TARGET_TYPE_OPTIONS.none,
    TARGET_TYPE_OPTIONS.feed,
    TARGET_TYPE_OPTIONS.link,
    TARGET_TYPE_OPTIONS.user,
    TARGET_TYPE_OPTIONS.explore,
    TARGET_TYPE_OPTIONS['explore-ebook-bundle'],
    TARGET_TYPE_OPTIONS.search,
    TARGET_TYPE_OPTIONS['explore-product-set'],
  ],
  square_cover: [
    TARGET_TYPE_OPTIONS.none,
    TARGET_TYPE_OPTIONS.feed,
    TARGET_TYPE_OPTIONS.link,
    TARGET_TYPE_OPTIONS.user,
    TARGET_TYPE_OPTIONS.explore,
    TARGET_TYPE_OPTIONS['explore-ebook-bundle'],
    TARGET_TYPE_OPTIONS.search,
  ],
  book_cover: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS.feed, TARGET_TYPE_OPTIONS.link, TARGET_TYPE_OPTIONS.user, TARGET_TYPE_OPTIONS.explore, TARGET_TYPE_OPTIONS.search],
  book_cover_with_banner: [
    TARGET_TYPE_OPTIONS.none,
    TARGET_TYPE_OPTIONS.feed,
    TARGET_TYPE_OPTIONS.link,
    TARGET_TYPE_OPTIONS.user,
    TARGET_TYPE_OPTIONS.explore,
    TARGET_TYPE_OPTIONS.search,
  ],
  book_cover_2: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS.feed, TARGET_TYPE_OPTIONS.link, TARGET_TYPE_OPTIONS.user, TARGET_TYPE_OPTIONS.explore, TARGET_TYPE_OPTIONS.search],
  main_banner: [
    TARGET_TYPE_OPTIONS.none,
    TARGET_TYPE_OPTIONS.feed,
    TARGET_TYPE_OPTIONS.link,
    TARGET_TYPE_OPTIONS.user,
    TARGET_TYPE_OPTIONS.explore,
    TARGET_TYPE_OPTIONS['explore-ebook-bundle'],
    TARGET_TYPE_OPTIONS.search,
  ],
  rank: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS.feed, TARGET_TYPE_OPTIONS.link, TARGET_TYPE_OPTIONS.user, TARGET_TYPE_OPTIONS.explore, TARGET_TYPE_OPTIONS.search],
  tag_cloud: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS.feed, TARGET_TYPE_OPTIONS.link, TARGET_TYPE_OPTIONS.user, TARGET_TYPE_OPTIONS.explore, TARGET_TYPE_OPTIONS.search],
  editor_review: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS.feed, TARGET_TYPE_OPTIONS.link, TARGET_TYPE_OPTIONS.user, TARGET_TYPE_OPTIONS.explore, TARGET_TYPE_OPTIONS.search],
  shortcut: [TARGET_TYPE_OPTIONS.none],
  marquee_announcement: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS['deep-link']],
  banner_full_width: [TARGET_TYPE_OPTIONS.none, TARGET_TYPE_OPTIONS['deep-link']],
};

const SectionBuilderFormObj: { [key in ISectionBuilderFields]: AnySchema<unknown, unknown, unknown> | Reference<unknown> | Lazy<never, unknown> } = {
  note: yup.string().required().trim(),
  layoutId: yup.string().required().trim(),
  gradientMode: yup.boolean(),
  title: yup.string().required().max(40).trim(),
  target: yup.string().oneOf(['None', 'Feed', 'Link', 'User', 'Explore', 'Search']), // TODO: update type
  configItems: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().trim(),
      }),
    )
    .required(),
  contentType: yup.string().trim(),
  // Marquee Announcement fields
  announcementText: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.MarqueeAnnouncement || val === 'marquee_announcement',
    then: (schema) => schema.required('Announcement text is required').trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  textLightColorHexcode: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.MarqueeAnnouncement || val === 'marquee_announcement',
    then: (schema) =>
      schema
        .default('#ffffff')
        .required('Text light color is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ffffff').notRequired(),
  }),
  textDarkColorHexcode: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.MarqueeAnnouncement || val === 'marquee_announcement',
    then: (schema) =>
      schema
        .default('#ffffff')
        .required('Text dark color is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ffffff').notRequired(),
  }),
  bgLightColorHexcode1: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.MarqueeAnnouncement || val === 'marquee_announcement',
    then: (schema) =>
      schema
        .default('#ff0000')
        .required('BG light color 1 is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ff0000').notRequired(),
  }),
  bgDarkColorHexcode1: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.MarqueeAnnouncement || val === 'marquee_announcement',
    then: (schema) =>
      schema
        .default('#ff0000')
        .required('BG dark color 1 is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ff0000').notRequired(),
  }),
  bgLightColorHexcode2: yup.string().when(['layoutId', 'gradientMode'], {
    is: (layoutId: string, gm: boolean) => (layoutId === ISectionLayoutId.MarqueeAnnouncement || layoutId === 'marquee_announcement') && gm === true,
    then: (schema) =>
      schema
        .default('#ff0000')
        .required('BG light color 2 is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ff0000').notRequired(),
  }),
  bgDarkColorHexcode2: yup.string().when(['layoutId', 'gradientMode'], {
    is: (layoutId: string, gm: boolean) => (layoutId === ISectionLayoutId.MarqueeAnnouncement || layoutId === 'marquee_announcement') && gm === true,
    then: (schema) =>
      schema
        .default('#ff0000')
        .required('BG dark color 2 is required')
        .matches(/^#([0-9A-Fa-f]{6})$/, 'Invalid hex code'),
    otherwise: (schema) => schema.default('#ff0000').notRequired(),
  }),
  imageUrl: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.BookCoverWithBanner,
    then: (schema) => schema.required('Image URL is required').trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  bannerFullWidthImageUrl: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.BannerFullWidth,
    then: (schema) => schema.required('Image URL is required').trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  dayBgImageUrl: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.BookCoverWithBanner,
    then: (schema) => schema.required('Day background image URL is required').trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  darkBgImageUrl: yup.string().when('layoutId', {
    is: (val: string) => val === ISectionLayoutId.BookCoverWithBanner,
    then: (schema) => schema.required('Dark background image URL is required').trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
};

export default SectionBuilderFormObj;
